import React, { useState, useEffect } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import Button from '@material-ui/core/Button';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import Typography from '@material-ui/core/Typography';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Divider from '@material-ui/core/Divider';
import AccordionActions from '@material-ui/core/AccordionActions';
import IconCheckbox from '../IconCheckbox';
import Filters from '../Filters/Filters';
import Alert from '@material-ui/lab/Alert';
import { Paper } from '@material-ui/core';

// Types dont un élément ne peut être rattaché qu'à UN SEUL compte : la case est grisée dès que
// la valeur est cochée sur un autre compte du même type, pour éviter de compter deux fois la
// même chose à l'export. 'product' en est volontairement exclu (un même type de produit peut
// être ventilé sur plusieurs comptes selon le taux de TVA).
const SINGLE_ACCOUNT_TYPES = [
	'payment',
	'offered',
	'discount',
	'_offered',
	'_discount',
	'vat',
	'cashback',
];

// Valeurs (id_table) déjà associées à un AUTRE compte du même type, indexées par le libellé du
// compte qui les détient pour pouvoir l'afficher en infobulle. Les clés sont normalisées en
// chaîne : côté TVA la valeur est un taux, tantôt nombre (liste des TVA) tantôt chaîne (retour
// base), comme le fait déjà la lecture des liaisons via les clés d'objet.
const getValuesUsedByOtherAccounts = (links, accountId, type, accountList) => {
	const used = new Map();
	if (!SINGLE_ACCOUNT_TYPES.includes(type)) return used;

	for (let otherAccountId in links) {
		if (otherAccountId == accountId) continue;
		for (let linkId in links[otherAccountId]) {
			const link = links[otherAccountId][linkId];
			if (!link || link.type != type) continue;
			const owner = accountList.find(acc => acc._id == otherAccountId);
			used.set(String(link.id_table), owner ? owner.label : null);
		}
	}

	return used;
};

export default function AccountsTable({
	type,
	accounts,
	vats,
	productTypes,
	paymentTypes,
	discounts,
	cashbackPaymentTypes,
	links,
	products,
	floors,
	onLinkChange,
	onDelete,
	onEdit,
	contacts,
	tags,
	productMode,
}) {
	const [currentAccountId, setCurrentAccountId] = useState(null);

	let [data, setData] = useState([]);
	let [_accounts, setAccount] = useState([]);

	useEffect(() => {
		setAccount(accounts[type] || []);
		switch (type) {
			case 'payment':
				setData(paymentTypes);
				break;
			case 'cashback':
				if (cashbackPaymentTypes && cashbackPaymentTypes.length) {
					setData(cashbackPaymentTypes);
				}
				break;
			case 'vat':
				setData(vats);
				break;
			case 'offered':
			case 'discount':
			case '_discount':
			case '_offered':
				setData(discounts);
				break;
			case 'product':
				setData(productMode == 'tag' ? tags : productTypes);
				break;
		}
	}, [type, productMode]);

	// Nombre d'éléments cochés pour un compte. On lit `links` (l'état vivant des liaisons) et non
	// a.linking, pour que le compteur suive les cases cochées avant même l'enregistrement. Le filtre
	// sur le type protège d'une liaison d'un autre type qui traînerait sur ce compte.
	const linkedCount = accountId =>
		Object.values(links[accountId] || {}).filter(l => !l || !l.type || l.type === type)
			.length;

	// Clé identifiant l'élément lié : le taux pour les comptes de TVA, l'id ailleurs.
	const keyIdTable = ['vat'].includes(type) ? 'tax_rate' : 'id';

	const onSelectAll = accountId => {
		// "Tout sélectionner" ne doit pas rattraper les valeurs déjà prises par un autre compte.
		const usedElsewhere = getValuesUsedByOtherAccounts(
			links,
			accountId,
			type,
			_accounts
		);
		let toInsert = [];
		for (let d of data) {
			if (usedElsewhere.has(String(d[keyIdTable]))) continue;
			if (
				!links[accountId] ||
				(links[accountId] && !links[accountId][d[keyIdTable]])
			) {
				toInsert.push(d[keyIdTable]);
			}
		}
		if (toInsert.length) onLinkChange(accountId, toInsert, type, true, []);
	};

	return (
		<>
			{_accounts &&
				_accounts.map((a, i) => {
					let labelFloors = '';
					if (type == 'product')
						if (a.all_floors) labelFloors = 'Toutes les salles';
						else
							labelFloors = a.floors
								.reduce((acc, id_floor) => {
									const f = floors.find(f => f.id == id_floor);
									if (f) acc.push(f.name);
									return acc;
								}, [])
								.join(', ');

					if (['achats', 'ventes', 'customer'].includes(type)) {
						const link = a.linking[0];
						if (link) {
							const ca = contacts.find(c => c.id == link.id_table); // check if customer account
							return (
								<Paper style={{ padding: 15 }}>
									<Typography>
										{`${a.label} (${a.account_number}) - identifiant : ${
											ca ? ca.name : link.id_table
										}`}{' '}
									</Typography>
									<Typography style={{ color: '#777' }}>
										{`Nombre d'élément(s) associé(s) : ${a.linking.length}`}
									</Typography>
									<Button
										size="small"
										color="primary"
										onClick={() => onEdit(a._id)}>
										Modifier compte
									</Button>
									<Button
										size="small"
										color="secondary"
										onClick={() => onDelete(a._id)}>
										Supprimer compte
									</Button>
								</Paper>
							);
						} else {
							return '';
						}
					} else {
						const usedElsewhere = getValuesUsedByOtherAccounts(
							links,
							a._id,
							type,
							_accounts
						);
						// Les valeurs prises par un autre compte sont hors jeu : "Tout sélectionner"
						// est coché dès que tout le reste l'est, sinon la case ne le serait jamais.
						const selectableRows = (data || []).filter(
							row => !usedElsewhere.has(String(row[keyIdTable]))
						);
						const allChecked =
							selectableRows.length > 0 &&
							selectableRows.every(
								row => links[a._id] && links[a._id][row[keyIdTable]]
							);

						return (
							<div key={`accoordion-${type}-${i}`}>
								<Accordion
									expanded={currentAccountId == a.id}
									onChange={() =>
										setCurrentAccountId(currentAccountId == a.id ? null : a.id)
									}>
									<AccordionSummary
										expandIcon={<ExpandMoreIcon />}
										id={`acummary-${a.id}`}>
										<Typography>{`${a.label} (${a.account_number}) - ${labelFloors}`}</Typography>
										<Typography
											style={{
												marginLeft: 'auto',
												paddingRight: 16,
												color: '#777',
											}}>
											{`Nombre d'élément(s) associé(s) : ${linkedCount(a._id)}`}
										</Typography>
									</AccordionSummary>
									<AccordionDetails>
										<div
											className="account-table-container"
											key={`cont-${a.id}`}>
											{![
												'payment',
												'offered',
												'discount',
												'_offered',
												'_discount',
											].includes(type) ? (
												<>
													<div
														className="account-table-row"
														style={{ display: 'flex' }}>
														<div className="account-table-row-name">
															<ListItemIcon
																onClick={_ => {
																	onSelectAll(a._id);
																}}>
																<IconCheckbox checked={allChecked} />
															</ListItemIcon>
															<p> Tout sélectionner </p>
														</div>
													</div>
													<hr />
												</>
											) : null}

											{data.map(row => {
												const link =
													links[a._id] && links[a._id][row[keyIdTable]]
														? links[a._id][row[keyIdTable]]
														: null;

												let classesRow = 'account-table-row';
												// Déjà coché sur un autre compte : on grise. La case reste
												// cliquable si elle est cochée ICI, pour pouvoir corriger
												// une configuration en doublon existante.
												const disabled =
													!link && usedElsewhere.has(String(row[keyIdTable]));
												const ownerLabel = disabled
													? usedElsewhere.get(String(row[keyIdTable]))
													: null;

												if (disabled)
													classesRow += ' account-table-row-disabled';

												let selectedTaxRates = [];
												if (link && link.all_tax_rates) {
													selectedTaxRates = [...vats];
												} else if (link && link.tax_rates) {
													selectedTaxRates = vats.filter(
														v =>
															link.tax_rates.find(tr => tr == v.tax_rate) !=
															null
													);
												}

												return (
													<div
														className={classesRow}
														key={`${row.type}-${row.id}-${a.id}`}
														id={`${row.type}-${row.id}-${a.id}`}
														title={
															disabled
																? `Déjà associé au compte ${ownerLabel ||
																		'ci-dessus'}`
																: undefined
														}
														style={{ display: 'flex' }}>
														<div className="account-table-row-name">
															<ListItemIcon
																onClick={_ =>
																	disabled
																		? null
																		: onLinkChange(
																				a._id,
																				row[keyIdTable],
																				type,
																				!!link ? false : true,
																				[]
																		  )
																}>
																<IconCheckbox checked={!!link} />
															</ListItemIcon>
															<p>{row.name} </p>
														</div>

														{type == 'product' && link ? (
															<div className="account-table-row-vat">
																<Filters
																	multiple={true}
																	monoSelect={false}
																	disabledClickAway={true}
																	label="Toutes les TVA"
																	datas={vats}
																	paramsLabel="name"
																	paramsValue="tax_rate"
																	selected={selectedTaxRates}
																	inputClassName="dataExport_filter_input"
																	inputLabelClassName="dataExport_input_label"
																	onClose={(data, pValue) => {
																		if (!!data) {
																			onLinkChange(
																				a._id,
																				row.id.toString(),
																				type,
																				false,
																				data.map(t => t.tax_rate)
																			);
																		}

																		return;
																	}}
																/>
															</div>
														) : null}
													</div>
												);
											})}
										</div>
									</AccordionDetails>
									<Divider />
									<AccordionActions>
										<Button
											size="small"
											color="primary"
											onClick={() => onEdit(a._id)}>
											Modifier compte
										</Button>
										<Button
											size="small"
											color="secondary"
											onClick={() => onDelete(a._id)}>
											Supprimer compte
										</Button>
									</AccordionActions>
								</Accordion>
							</div>
						);
					}
				})}

			{!_accounts || (_accounts && !_accounts.length) ? (
				<div style={{ marginTop: 20 }}>
					<Alert severity="info">
						Aucun compte comptable de ce type n'est configuré
					</Alert>
				</div>
			) : null}
		</>
	);
}
